import React, { useRef, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Bounds,
  CameraShake,
  Center,
  ContactShadows,
  Environment,
  OrbitControls,
  Sky,
  useBounds,
  useGLTF,
  useTexture,
} from "@react-three/drei";
import * as THREE from "three";
import dat from "dat.gui"; // Voeg dat.gui toe
import { Line, LineBasicMaterial, BufferGeometry, Vector3 } from "three";
import { GaasMaterial } from "./GaasMateriaal";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { formConfig } from "../form/formConfig";
import { useSpring, a } from "@react-spring/three";
import { FogEffect, Grass } from "./Grass";
import { Tiles } from "./Tiles";
import { Boom } from "./Boom";
import { Light } from "./Light";
import { material_list } from "./materials/material_list";
import { useWebSocket } from "../hooks/useWebSocket";
import { BackendModel } from "./BackendModel";
import { mapFormToBackend } from "../utils/mapFormToBackend";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useLoading } from "../contexts/LoadingContext";
import { useTexturePreloader } from "../hooks/useTexturePreloader";



const WindowModel = () => {


/*
  const path = "./data/american_oak_1-1K/1K-american_oak_1_";

  const [colorMap, normalMap, roughnessMap, displacementMap] = useTexture([
    path + 'basecolor.png',
path + 'normal.png',
path + 'roughness.png',
path + 'ao.png'
]);

const repeatFactor = 0.1; // Verander dit naar 20 of 30 als het nog te groot is
[colorMap, normalMap, roughnessMap, displacementMap].forEach((map) => {
  if (map) {
    map.wrapS = map.wrapT = THREE.RepeatWrapping;
    map.repeat.set(repeatFactor, repeatFactor);
  }
});

const newWoodMaterial = new THREE.MeshStandardMaterial({
  map:colorMap ,
  normalMap:normalMap,
  roughnessMap:roughnessMap,
  displacementMap:displacementMap,
  
});
*/

  const modelRef = useRef();
  const { scene } = useGLTF("/poolhouse_v2.glb"); // Zorg ervoor dat het pad correct is
  const { WIDTH, HEIGHT } = useWatch({ name: "DIMENSION" });
  const formColors = useWatch() ; // Voor de framekleur

  console.log(formColors)

  // Je code voor materiaalupdating
  useEffect(() => {
    if (!scene) return;

    // Update materialen in het 3D-model
    scene.traverse((child) => {
      if (child.isMesh) {
        // kijken welk materiaal we moeten aanpassen
        const m = material_list.find((m) => m.name == child.material.name) || null;
        
        if (m) {
          // m.material -> materiaal dat moet aangepast worden!
          console.log(m)
          const hex_color = formConfig.find((x) => x.name == m.variable).options.find(x => x.code == formColors[m.variable]).color;
          child.material = new THREE.MeshStandardMaterial({
            ...m.material,
            color: hex_color,
            envMapIntensity: 1.2
          });
          //child.material = newWoodMaterial;
          // Schaduwen inschakelen
          child.castShadow = true;
          child.receiveShadow = true;
        }else{
          //child.material = new THREE.MeshStandardMaterial({color: "#FF0000"})
        }

        if (child.geometry && child.geometry.attributes.position) {
          child.geometry.computeVertexNormals();
        }

      }
    });
  }, [formColors, WIDTH, HEIGHT]);

  // Update morphTargetInfluences en schaduwen na veranderingen in dimensies
  useEffect(() => {
    if (!scene || !WIDTH || !HEIGHT) return;


    // Pas morphTargetInfluences aan op basis van 'dimensions'
    scene.traverse((child) => {
      if (child.isMesh && child.morphTargetInfluences) {
        const heightKeyshape = Math.max(
          0,
          Math.min(1, (HEIGHT - 500) / (2500 - 500))
        );
        const widthKeyshape = Math.max(
          0,
          Math.min(1, (WIDTH - 500) / (1500 - 500))
        );

        // Update de morfdoelen
        if (child.morphTargetInfluences[0] !== undefined)
          child.morphTargetInfluences[0] = heightKeyshape;
        if (child.morphTargetInfluences[1] !== undefined)
          child.morphTargetInfluences[1] = widthKeyshape;

        // Normale vectoren opnieuw berekenen voor schaduwen
        if (child.geometry && child.geometry.attributes.position) {
          child.geometry.computeVertexNormals();
        }
      }
    });
  }, [WIDTH, HEIGHT, scene]);

  return <primitive   ref={modelRef} object={scene} />;
};

export default WindowModel;

const interpolateFunc = (t) => 1 - Math.exp(-5 * t) + 0.007 * t; // Matches the default Bounds behavior
const interpolateFunc1 = (t) => -t * t * t + 2 * t * t; // Start smoothly, finish linearly
const interpolateFunc2 = (t) => -t * t * t + t * t + t; // Start linearly, finish smoothly

// Component to preload textures inside Canvas context
const TexturePreloaderComponent = ({ onLoadingComplete }) => {
  useTexturePreloader(onLoadingComplete);
  return null;
};

export const ConfiguratorContainer = () => {
  console.log("rerender 3D configurator");
  const [spring, api] = useSpring(() => ({
    color: "white",
    config: { tension: 280, friction: 60 },
  }));

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [enableWebSocket, setEnableWebSocket] = useState(false);
  const { setIsConfiguratorLoading } = useLoading();
  const loadingStartTimeRef = useRef(Date.now());

  // Defer WebSocket until page is fully loaded
  useEffect(() => {
    // Wait for page load to complete before starting WebSocket
    const handleLoad = () => {
      setTimeout(() => {
        console.log('🚀 Page loaded, enabling WebSocket...');
        setEnableWebSocket(true);
      }, 500); // Additional delay after page load
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  // WebSocket connection for backend communication
  // Only connect after page is fully loaded
  const { objData, messages, isConnected, sendMessage } = useWebSocket("wss://ws.koto.build/ws", {
    enabled: enableWebSocket
  });

  // Watch form values to send to backend
  const formValues = useWatch();
  const debounceTimeoutRef = useRef(null);
  const lastSentParamsRef = useRef(null);

  // Callback when all textures are loaded
  // Ensures minimum 5 seconds loading time to show the animation
  const handleLoadingComplete = () => {
    const elapsedTime = Date.now() - loadingStartTimeRef.current;
    const minimumLoadingTime = 5000; // 5 seconds
    const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);

    console.log(`🎉 Loading complete! Elapsed: ${elapsedTime}ms, Waiting additional: ${remainingTime}ms`);

    setTimeout(() => {
      console.log('✨ Hiding spinner now...');
      setIsLoading(false);
      setIsConfiguratorLoading(false);
    }, remainingTime);
  };

  // Send parameters to backend when form values change
  useEffect(() => {
    if (!isConnected) return;

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the backend call
    debounceTimeoutRef.current = setTimeout(() => {
      // Map formConfig to backend format using the dynamic mapper
      const backendParams = mapFormToBackend(formValues);

      // Only send if params have actually changed
      const paramsString = JSON.stringify(backendParams);
      if (paramsString !== lastSentParamsRef.current) {
        lastSentParamsRef.current = paramsString;
        sendMessage({
          action: "generate",
          model: "carport",
          parameters: backendParams
        });
      }
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [formValues, isConnected, sendMessage]);

  return (
    <div style={{ position: 'relative', width: '100%', flex: 1 }}>
      {isLoading && <LoadingSpinner />}
      <Canvas
        gl={{
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 0.8,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        shadows={{ type: THREE.PCFSoftShadowMap }}
        style={{
          width: "100%",
          height: "100%",
          filter: isLoading ? '' : 'none',
          transition: 'filter 0.5s ease-in-out'
        }}
      >
        {/* Preload all textures immediately when Canvas loads */}
        <TexturePreloaderComponent onLoadingComplete={handleLoadingComplete} />

        <OrbitControls
          makeDefault
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI / 2.2}
          minDistance={8}
          maxDistance={40}
          zoomSpeed={0.8}
          rotateSpeed={0.6}
          enablePan={false}
          enableDamping={true}
          dampingFactor={0.08}
          mouseButtons={{
            LEFT: 0,
            MIDDLE: 1,
            RIGHT: 2
          }}
        />

        {/* Sky and Environment - using optimized local HDR (1.4MB) */}
        <Light sunPosition={[-50, 30, 30]} turbidity={0.1} rayleigh={0.1} />
        <Environment files="/venice_sunset_1k.hdr" background blur={0.5} />

        {/* Main sun light - strong directional with enhanced shadows */}
        <directionalLight
          position={[15, 40, 10]}
          intensity={2.5}
          castShadow
          shadow-mapSize-width={8192}
          shadow-mapSize-height={8192}
          shadow-camera-far={200}
          shadow-camera-left={-100}
          shadow-camera-right={100}
          shadow-camera-top={100}
          shadow-camera-bottom={-100}
          shadow-camera-near={0.1}
          shadow-bias={-0.00005}
          shadow-normalBias={0.02}
          shadow-radius={3}
          color="#ffffff"
        />

        {/* Fill lights for softer shadows */}
        <directionalLight position={[-8, 8, -8]} intensity={0.8} color="#e8f4ff" />
        <directionalLight position={[5, 5, -15]} intensity={0.5} color="#fff5e8" />

        {/* Ambient light - very low */}
        <ambientLight intensity={0.15} color="#b3d9ff" />

        {/* Hemisphere for sky/ground ambient */}
        <hemisphereLight args={['#87CEEB', '#5a4a3a', 0.3]} />

        {/* Contact shadows for enhanced ground realism */}
        <ContactShadows
          position={[0, -1.59, 0]}
          opacity={0.4}
          scale={40}
          blur={2}
          far={10}
          resolution={1024}
          color="#000000"
        />

        {/* Render backend geometry if available */}
        {objData && <BackendModel objData={objData} />}
      </Canvas>
    </div>
  );
};
